import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";

const NAVY_DEEP = "#07182E";
const PAPER = "#F4F1E8";
const PAPER_2 = "#FBFAF5";
const GOLD = "#C9A24B";
const INK = "#14213A";
const INK_2 = "#545C6B";
const LINE = "#E4DECF";

const FONT_SANS = "'IBM Plex Sans', system-ui, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, monospace";
const FONT_SERIF = "'Spectral', Georgia, serif";

const STORAGE_KEY = "cd-cookie-consent-v1";

type Choice = "all" | "essential";

export function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (!v) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  function decide(choice: Choice) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, ts: new Date().toISOString() }),
      );
    } catch {
      /* ignore */
    }
    setOpen(false);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      style={{
        position: "fixed",
        left: 20,
        right: 20,
        bottom: 20,
        zIndex: 70,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          pointerEvents: "auto",
          maxWidth: 560,
          width: "100%",
          background: PAPER_2,
          border: `1px solid ${LINE}`,
          borderTop: `3px solid ${GOLD}`,
          borderRadius: 12,
          boxShadow: "0 18px 50px rgba(7,24,46,0.22)",
          padding: "22px 24px 20px",
          fontFamily: FONT_SANS,
          color: INK,
        }}
      >
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: GOLD,
            marginBottom: 8,
          }}
        >
          Cookies
        </div>
        <div
          style={{
            fontFamily: FONT_SERIF,
            fontSize: 20,
            lineHeight: 1.2,
            letterSpacing: "-.01em",
            marginBottom: 8,
          }}
        >
          We value your privacy.
        </div>
        <p
          style={{
            margin: 0,
            fontSize: 13.5,
            lineHeight: 1.55,
            color: INK_2,
          }}
        >
          We use essential cookies to run CapitalDart and optional analytics
          cookies to improve the product. Read our{" "}
          <Link
            to="/privacy"
            style={{
              color: INK,
              borderBottom: `2px solid ${GOLD}`,
              textDecoration: "none",
              paddingBottom: 1,
            }}
          >
            privacy policy
          </Link>
          .
        </p>
        <div
          style={{
            marginTop: 18,
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={() => decide("essential")}
            style={{
              fontFamily: FONT_SANS,
              fontSize: 13.5,
              fontWeight: 600,
              color: INK,
              background: PAPER,
              border: `1px solid ${LINE}`,
              padding: "10px 16px",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => decide("all")}
            style={{
              fontFamily: FONT_SANS,
              fontSize: 13.5,
              fontWeight: 600,
              color: NAVY_DEEP,
              background: GOLD,
              border: "none",
              padding: "10px 18px",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}